import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { User, CheckSquare, Square, Merge, X, RefreshCw } from 'lucide-react'
import { artists } from '../api/client'
import type { Artist } from '../types'

export default function Artists() {
  const queryClient = useQueryClient()
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [targetArtist, setTargetArtist] = useState<Artist | null>(null)

  // Use browse endpoint to show ALL artists (not filtered by collection)
  const { data: artistList, isLoading } = useQuery({
    queryKey: ['artists', 'browse'],
    queryFn: () => artists.browse(1000, 0),
  })

  const mergeMutation = useMutation({
    mutationFn: async ({ sourceIds, targetId }: { sourceIds: string[]; targetId: string }) => {
      let totalAlbums = 0
      let totalTracks = 0
      for (const sourceId of sourceIds) {
        if (sourceId !== targetId) {
          const result = await artists.merge(sourceId, targetId)
          totalAlbums += result.albums_moved
          totalTracks += result.tracks_moved
        }
      }
      return { albums_moved: totalAlbums, tracks_moved: totalTracks }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['artists', 'browse'] })
      setSelectMode(false)
      setSelectedIds(new Set())
      setShowMergeModal(false)
      setTargetArtist(null)
      alert(`Merged successfully! Moved ${result.albums_moved} albums and ${result.tracks_moved} tracks.`)
    },
    onError: (error) => {
      console.error('Merge failed:', error)
      alert('Failed to merge artists. Please try again.')
    },
  })

  const refreshAllMutation = useMutation({
    mutationFn: artists.refreshAll,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['artists', 'browse'] })
      alert(`Refreshed ${result.updated} of ${result.total} artists with new metadata and images.`)
    },
    onError: (error) => {
      console.error('Refresh failed:', error)
      alert('Failed to refresh artists. Please try again.')
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const selectedArtists = artistList?.filter((a) => selectedIds.has(a.id)) || []

  const handleMergeClick = () => {
    if (selectedArtists.length < 2) return
    setShowMergeModal(true)
  }

  const handleConfirmMerge = () => {
    if (!targetArtist) return
    const sourceIds = Array.from(selectedIds)
    mergeMutation.mutate({ sourceIds, targetId: targetArtist.id })
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Artists</h1>
        <div className="flex items-center gap-2">
          {selectMode && selectedIds.size >= 2 && (
            <button
              onClick={handleMergeClick}
              className="btn btn-primary flex items-center gap-2"
            >
              <Merge size={18} />
              Merge ({selectedIds.size})
            </button>
          )}
          {!selectMode && (
            <button
              onClick={() => {
                if (confirm('Refresh metadata for all artists? This will fetch images and bios from Wikipedia/MusicBrainz. This may take a while for large libraries.')) {
                  refreshAllMutation.mutate()
                }
              }}
              disabled={refreshAllMutation.isPending}
              className="btn btn-secondary flex items-center gap-2"
              title="Fetch images and metadata for all artists"
            >
              <RefreshCw size={18} className={refreshAllMutation.isPending ? 'animate-spin' : ''} />
              {refreshAllMutation.isPending ? 'Refreshing...' : 'Refresh All'}
            </button>
          )}
          <button
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={`btn ${selectMode ? 'btn-secondary' : 'btn-secondary'} flex items-center gap-2`}
          >
            {selectMode ? (
              <>
                <X size={18} />
                Cancel
              </>
            ) : (
              <>
                <CheckSquare size={18} />
                Select
              </>
            )}
          </button>
        </div>
      </div>

      {selectMode && (
        <div className="mb-4 p-3 bg-dark-800 rounded-lg text-sm text-dark-300">
          Select 2 or more artists to merge them. The merged artist will keep all albums and tracks.
        </div>
      )}

      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : artistList && artistList.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {artistList.map((artist) => {
            const isSelected = selectedIds.has(artist.id)
            return selectMode ? (
              <div
                key={artist.id}
                onClick={() => toggleSelect(artist.id)}
                className={`card group cursor-pointer transition-colors ${
                  isSelected ? 'bg-primary-600/20 ring-2 ring-primary-500' : 'hover:bg-dark-700'
                }`}
              >
                <div className="absolute top-2 right-2 z-10">
                  {isSelected ? (
                    <CheckSquare size={24} className="text-primary-500" />
                  ) : (
                    <Square size={24} className="text-dark-500" />
                  )}
                </div>
                <div className="aspect-square mb-4 bg-dark-700 rounded-full flex items-center justify-center overflow-hidden">
                  {artist.image_path ? (
                    <img
                      src={`/api/image/${encodeURIComponent(artist.image_path)}`}
                      alt={artist.name}
                      className="w-full h-full object-cover" loading="lazy"
                    />
                  ) : (
                    <User size={48} className="text-dark-500" />
                  )}
                </div>
                <h3 className="font-medium text-center truncate">{artist.name}</h3>
              </div>
            ) : (
              <Link
                key={artist.id}
                to={`/artists/${artist.id}`}
                className="card group hover:bg-dark-700 transition-colors"
              >
                <div className="aspect-square mb-4 bg-dark-700 rounded-full flex items-center justify-center overflow-hidden">
                  {artist.image_path ? (
                    <img
                      src={`/api/image/${encodeURIComponent(artist.image_path)}`}
                      alt={artist.name}
                      className="w-full h-full object-cover" loading="lazy"
                    />
                  ) : (
                    <User size={48} className="text-dark-500" />
                  )}
                </div>
                <h3 className="font-medium text-center truncate group-hover:text-primary-400">
                  {artist.name}
                </h3>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="card text-center py-12">
          <User size={48} className="mx-auto text-dark-500 mb-4" />
          <p className="text-dark-400 text-lg mb-2">No artists yet</p>
          <p className="text-dark-500 text-sm">
            Scan a music library to see artists here
          </p>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Merge Artists</h2>
            <p className="text-dark-300 mb-4">
              Select which artist to keep. All albums and tracks from the other artists will be
              moved to this artist.
            </p>
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {selectedArtists.map((artist) => (
                <button
                  key={artist.id}
                  onClick={() => setTargetArtist(artist)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    targetArtist?.id === artist.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-700 hover:bg-dark-600'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-dark-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {artist.image_path ? (
                      <img
                        src={`/api/image/${encodeURIComponent(artist.image_path)}`}
                        alt={artist.name}
                        className="w-full h-full object-cover" loading="lazy"
                      />
                    ) : (
                      <User size={20} className="text-dark-400" />
                    )}
                  </div>
                  <span className="font-medium truncate">{artist.name}</span>
                  {targetArtist?.id === artist.id && (
                    <span className="ml-auto text-sm bg-primary-700 px-2 py-0.5 rounded">Keep</span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMergeModal(false)
                  setTargetArtist(null)
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMerge}
                disabled={!targetArtist || mergeMutation.isPending}
                className="btn btn-primary flex-1 disabled:opacity-50"
              >
                {mergeMutation.isPending ? 'Merging...' : 'Merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
